/**
 * JarvisAvatarHeader
 *
 * Container im CRM-Header. Drei Zustands-Pfade:
 *
 *  1. User-Org ist nicht HEYKIKI       → null (Header rendert normales Logo)
 *  2. HEYKIKI aber jarvisAktiv=false   → fallbackLogo (statisches HeyKiki-PNG)
 *  3. HEYKIKI mit jarvisAktiv=true     → animierter Avatar + Voice-Connection
 *
 * Voice-Pfad (3.) wrapped sich selbst in einen `ConversationProvider` von
 * `@elevenlabs/react` — der Hook `useConversation` darf nur innerhalb dieses
 * Providers laufen. Wir mounten den Provider also nur, wenn wir wirklich
 * im Voice-Modus sind — kein unnötiger Provider in der App-Root.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useConversation } from '@elevenlabs/react';
import { useAuth } from '../../../context/AuthContext';
import { useJarvisStatus } from './useJarvisStatus';
import JarvisAvatar from './JarvisAvatar';
import useJarvisLiveEvents from './useJarvisLiveEvents';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Größere Avatar-Box damit Kiki im Header wirklich präsent wird. Header
// wächst mit der Avatar-Höhe mit. `self-end` setzt Avatar auf die untere
// Container-Kante; `-mb-2 lg:-mb-4` gleicht das Header-Padding (py-2 lg:py-4)
// aus, sodass Avatar's untere Kante exakt auf der Header-Trennlinie sitzt
// — Kiki „steht" auf der Linie statt darüber zu schweben.
const SIZE_CLASSES = 'h-28 lg:h-36 xl:h-40 aspect-square self-end -mb-2 lg:-mb-4';

export default function JarvisAvatarHeader({ fallbackLogo = '/heykiki-logo.png' }) {
  const { user } = useAuth();
  const { status, loading } = useJarvisStatus();

  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  if (!isHeykikiOrg) return null;

  // Während Status lädt oder nicht aktiv: statisches Logo
  // (vermeidet Layout-Shift bei Standalone-Subs).
  if (loading || !status.aktiv) {
    return (
      <img
        src={fallbackLogo}
        alt="HeyKiki"
        className={`${SIZE_CLASSES} object-contain`}
      />
    );
  }

  // Der ConversationProvider sitzt auf App-Root-Ebene (siehe
  // JarvisRootProvider in App.jsx). Dadurch überlebt die Voice-Session
  // jeden Routen-Wechsel.
  return <JarvisVoiceUI />;
}

// ============================================================
// JarvisVoiceUI — Click öffnet/schließt ElevenLabs-Voice-Session
// ============================================================

function JarvisVoiceUI() {
  const [avatarState, setAvatarState] = useState('idle');
  // Verhindert Doppel-Clicks während StartSession async läuft.
  const [starting, setStarting] = useState(false);

  const conv = useConversation({
    onConnect: () => {
      // Während aktiver Voice-Session aber Susi gerade nicht spricht,
      // bleiben wir auf der ruhigen Idle-Animation (nur Atmen) — nicht
      // auf Listening, weil der Listening-Loop subtile Mund-Bewegungen
      // hat die in Sprechpausen verwirrend wirken. Speaking-Animation
      // wird vom Audio-Polling unten nur dann eingeschaltet, wenn Susi
      // wirklich Sound produziert.
      setAvatarState('idle');
    },
    onDisconnect: () => {
      setAvatarState('idle');
      // Instant-Memory: direkt nach dem Disconnect das Transcript via
      // ElevenLabs-API holen und Summary in DB schreiben. Schneller als
      // auf den Post-Call-Webhook zu warten (der 2-5 Min Latenz hat) —
      // wichtig, wenn der User sofort eine neue Session startet und
      // sich Susi erinnern soll.
      try {
        const conversationId = conv?.getId?.();
        if (conversationId) {
          const token = localStorage.getItem('token');
          axios.post(
            `${API_URL}/heykiki/jarvis/conversation-end`,
            { conversationId },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => { /* Webhook ist Fallback, fail silently */ });
        }
      } catch { /* ignore */ }
    },
    onError: (err) => {
      console.error('[Jarvis] conversation error:', err);
      toast.error(err?.message || 'Voice-Verbindung fehlgeschlagen');
      setAvatarState('idle');
    },
    // Wir nutzen onModeChange NICHT mehr für den Avatar-Switch — das SDK
    // hält `speaking` auch durch Sprechpausen hindurch. Stattdessen
    // pollen wir unten den echten Output-Volume und mappen das auf das
    // Speaking-Loop. So bewegt sich Kiki's Mund wirklich nur wenn Audio
    // rauskommt.
  });

  const isConnected = conv.status === 'connected' || conv.status === 'connecting';

  // ============================================================
  // Audio-Output-Polling — Speaking-Animation an echtem Sound koppeln
  // ============================================================
  //
  // Strategy:
  //  - Während Session aktiv ist, frame-by-frame `getOutputVolume()` lesen
  //  - Volume > THRESHOLD → state = 'speaking'
  //  - Volume <= THRESHOLD länger als HOLD_MS → state = 'listening'
  //  - Kleine Hysterese (HOLD_MS) damit kurze Atempausen zwischen Worten
  //    nicht ständig zwischen den Loops hin-und-her switchen.
  const lastSpeakingTsRef = useRef(0);
  // Letzte Audio-Aktivität (Susi ODER User) — für den Hard-Stop nach 60s
  // Stille. Wird sowohl im Output- als auch Input-Polling aktualisiert.
  const lastActivityTsRef = useRef(0);
  // Wrapper-Ref um den Avatar — bekommt pro Frame die `--mic-intensity`
  // CSS-Var aus dem User-Mic-Volume gesetzt (siehe tick()). Dadurch
  // pulsiert ein Glow in der Akzentfarbe um Kiki herum sobald der User
  // spricht. Direktes DOM-Schreiben statt React-State, weil 60fps-Updates
  // sonst die ganze Komponente neu rendern würden.
  const micGlowRef = useRef(null);
  // Geglättete Intensitäten pro Soundwave-Band (5 Bars). Schneller
  // Anstieg, gemäßigteres Abklingen — siehe tick() unten.
  const micIntensityRef = useRef([0, 0, 0, 0, 0]);

  useEffect(() => {
    if (!isConnected) {
      lastActivityTsRef.current = 0;
      return;
    }
    lastActivityTsRef.current = performance.now();
    const OUTPUT_THRESHOLD = 8;
    const INPUT_THRESHOLD = 12; // Mic ist meistens etwas rauschiger als TTS-Output
    const HOLD_MS = 280;
    let raf;
    const tick = () => {
      try {
        // --- Output: Susi spricht? ---
        let outAvg = 0;
        const outData = conv.getOutputByteFrequencyData?.();
        if (outData && outData.length) {
          let sum = 0;
          for (let i = 0; i < outData.length; i++) sum += outData[i];
          outAvg = sum / outData.length;
        } else {
          outAvg = (conv.getOutputVolume?.() ?? 0) * 255;
        }
        // --- Input: User spricht? ---
        let inAvg = 0;
        const inData = conv.getInputByteFrequencyData?.();
        if (inData && inData.length) {
          let sum = 0;
          for (let i = 0; i < inData.length; i++) sum += inData[i];
          inAvg = sum / inData.length;
        } else {
          inAvg = (conv.getInputVolume?.() ?? 0) * 255;
        }

        const now = performance.now();
        if (outAvg > OUTPUT_THRESHOLD) {
          lastSpeakingTsRef.current = now;
          lastActivityTsRef.current = now;
          setAvatarState((s) => (s === 'speaking' ? s : 'speaking'));
        } else if (now - lastSpeakingTsRef.current > HOLD_MS) {
          // Susi schweigt → zurück auf ruhige Idle-Animation (nur Atmen).
          setAvatarState((s) => (s === 'idle' ? s : 'idle'));
        }
        if (inAvg > INPUT_THRESHOLD) {
          lastActivityTsRef.current = now;
        }

        // === Soundwave-Bars: 5 Frequenz-Bänder aus inData extrahieren ===
        // inData hat typischerweise 32–128 Bins (ElevenLabs gibt eine
        // brauchbare Spektrum-Repräsentation des Mic-Inputs zurück). Wir
        // teilen das Array in 5 etwa gleichgroße Bands auf und mitteln pro
        // Band — gibt jedem Bar eine eigene Reaktion auf Bass/Mid/Treble.
        // Wenn `inData` mal fehlt (SDK-Edge-Case), füllen wir alle 5 Bars
        // gleichmäßig aus `inAvg` damit der Effekt nicht einfach tot ist.
        const bars = [0, 0, 0, 0, 0];
        if (inData && inData.length >= 5) {
          const bandSize = Math.floor(inData.length / 5);
          for (let b = 0; b < 5; b++) {
            let sum = 0;
            const from = b * bandSize;
            const to = b === 4 ? inData.length : from + bandSize;
            for (let i = from; i < to; i++) sum += inData[i];
            const bandAvg = sum / (to - from);
            // Threshold + Normalisierung auf 0..1, identisch zur Gesamt-
            // Intensity damit Stille = 0 und lautes Sprechen = ~1.
            bars[b] = Math.max(0, Math.min(1, (bandAvg - INPUT_THRESHOLD) / 60));
          }
        } else {
          const fallback = Math.max(0, Math.min(1, (inAvg - INPUT_THRESHOLD) / 60));
          bars.fill(fallback);
        }

        // Smoothing pro Bar — schneller Anstieg, gemäßigteres Abklingen,
        // sonst flackert es stroboskopisch bei jeder Phonem-Grenze.
        if (micGlowRef.current) {
          for (let b = 0; b < 5; b++) {
            const prev = micIntensityRef.current[b] ?? 0;
            const target = bars[b];
            const smoothed = target > prev
              ? prev + (target - prev) * 0.55
              : prev + (target - prev) * 0.2;
            micIntensityRef.current[b] = smoothed;
            micGlowRef.current.style.setProperty(`--bar-${b + 1}`, smoothed.toFixed(3));
          }
        }
      } catch { /* SDK noch nicht ready oder Tab inactive — ignore */ }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isConnected, conv]);

  // ============================================================
  // Live-Event-Polling: proaktive Mitteilungen an Kiki pushen
  // ============================================================
  //
  // Während die Session läuft, pollen wir alle 8s das Backend nach neuen
  // Anfragen / Anrufen / Notifications und reichen sie an Kiki weiter
  // (sendContextualUpdate). Sie entscheidet dann selbst ob sie unterbricht
  // oder das Thema später anschneidet — Prompt-Regel in
  // jarvisAgentService steuert das.
  useJarvisLiveEvents({ isConnected, conversation: conv });

  // ============================================================
  // Hard-Stop-Watchdog: 60s ohne Audio-Aktivität → Session beenden
  // ============================================================
  //
  // Verhindert Geister-Sessions (User vergisst zu disconnecten, lässt Tab
  // offen) und vermeidet dass ElevenLabs nach längerer Stille selbst
  // anfängt zu fragen. Wir tracken die letzte Audio-Aktivität von beiden
  // Seiten (Susi-Output + User-Mic-Input).
  useEffect(() => {
    if (!isConnected) return;
    const HARD_STOP_MS = 60_000;
    const id = setInterval(() => {
      const since = performance.now() - lastActivityTsRef.current;
      if (since > HARD_STOP_MS) {
        console.log('[Jarvis] Hard-Stop nach 60s Stille — Session wird beendet');
        try { conv.endSession(); } catch { /* */ }
      }
    }, 3000);
    return () => clearInterval(id);
  }, [isConnected, conv]);

  const handleClick = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Aktive Session beenden
    if (isConnected) {
      conv.endSession();
      return;
    }

    if (starting) return;
    setStarting(true);
    setAvatarState('thinking');

    try {
      // 1) Backend: signed URL ziehen
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_URL}/heykiki/jarvis/session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { signed_url, dynamic_variables } = r.data || {};
      if (!signed_url) throw new Error('Keine signed_url vom Server');

      // 2) Conversation öffnen (Mic-Permission wird vom SDK angefragt)
      await conv.startSession({
        signedUrl: signed_url,
        dynamicVariables: dynamic_variables || {},
      });
      // onConnect setzt avatarState auf 'listening'
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Unbekannter Fehler';
      toast.error(`MyKiki konnte nicht starten: ${msg}`);
      setAvatarState('idle');
    } finally {
      setStarting(false);
    }
  }, [isConnected, starting, conv]);

  // Layout-Trick: ein `<span>` wrapper bekommt sämtliche Layout-Classes
  // (Größe + flex-self-end + Margin-Tricks für die Header-Linie). Innen
  // sitzt der JarvisAvatar mit `w-full h-full` — der nimmt also die volle
  // Wrapper-Größe ein. Daneben (im DOM, hinter dem Avatar im Stacking)
  // sitzt das Glow-Element als absolut positioniertes `<span>` mit
  // pointer-events: none. Sichtbar nur wenn Voice-Session aktiv.
  //
  // Wichtig: kein `isolation: isolate` und kein z-index < 0 — das hat
  // beim ersten Versuch den Avatar versteckt. Hier nur explicit z-index
  // 10 auf Avatar, default (0) auf Glow → Glow sitzt klar HINTER dem
  // Avatar, der hat `background: white` und verdeckt den Glow innerhalb
  // seines Quadrats. Der Glow ragt 14px+ nach außen — DA ist er sichtbar.
  // Soundwave-Bars neben den Ohren visualisieren das Mikro-Input in der
  // Akzentfarbe. Wrapper trägt die CSS-Vars (--bar-1..--bar-5), die im
  // raf-tick aus `getInputByteFrequencyData` gesetzt werden — jede Bar
  // liest ihre Stärke und passt Höhe + Opacity + Glow dynamisch an. Bei
  // Stille atmet ein dezenter Grundpuls (Mikro-Scharf-Signal); beim
  // Sprechen wachsen die Bars asymmetrisch je nach Frequenz-Band.
  // Bars werden IMMER gerendert (nicht conditional), damit die CSS-Fade-
  // Out-Transition beim Beenden der Session noch durchläuft. Sichtbarkeit
  // läuft über die `is-active`-Klasse am Wrapper — CSS-Selector
  // `.jarvis-mic-glow.is-active .jarvis-soundwaves` toggelt Opacity +
  // Slide. Würde man `{isConnected && ...}` machen, fliegt das Element
  // sofort aus dem DOM und die Transition käme nie zum Zug.
  return (
    <span
      ref={micGlowRef}
      className={`${SIZE_CLASSES} jarvis-mic-glow relative inline-block ${isConnected ? 'is-active' : ''}`}
    >
      <span className="jarvis-soundwaves jarvis-soundwaves-left" aria-hidden="true">
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
      </span>
      <span className="jarvis-soundwaves jarvis-soundwaves-right" aria-hidden="true">
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
        <span className="jarvis-soundwave-bar" />
      </span>
      <JarvisAvatar
        state={avatarState}
        onClick={handleClick}
        className="w-full h-full relative z-10 cursor-pointer hover:brightness-110 transition-[filter]"
      />
    </span>
  );
}
